import { createSelector } from 'reselect'
import { defineMessage } from 'react-intl'
import { getHotelOfferUrl } from 'lib/offer/offerPageURL'
import { excludeNullOrUndefined, getAccommodationDesignationWithCruise, getAccommodationDesignationWithTourBundle } from 'checkout/utils'
import { getCurrentIntl } from 'i18n/util/currentIntl'
import { groupBy, nonNullable } from 'lib/array/arrayUtils'
import { diffInDays } from 'lib/datetime/dateUtils'
import { OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_BED_BANK } from 'constants/offer'
import { queryKeyCheckIn, queryKeyCheckOut } from 'constants/url'
import { RESERVATION_TYPE_INSTANT_BOOKING } from 'constants/reservation'
import { getCruiseItems } from 'checkout/selectors/cruiseSelectors'
import { getTourV2Items } from 'checkout/selectors/view/toursv2'
import getFlightBundledItemIds from '../getFlightBundledItemIds'
import { getAccommodationV2ItemService } from 'lib/checkout/checkoutUtils'
import getAccommodationV2Items from '../items/getAccommodationV2Items'
import { getAccommodationV2ItemView } from 'checkout/lib/utils/accommodationV2/view'
import { getLppMemberPricingEnabledForCheckout } from 'luxPlus/selectors/featureToggle'

const nightCountMessage = defineMessage({
  id: '25YvTg51zMS3bIjLYgo7',
  defaultMessage: '{count, plural, one {# night} other {# nights}}',
})

const getAccommodationV2ItemViews = createSelector(
  (state: App.State) => getAccommodationV2Items(state),
  (state: App.State) => state.offer.properties,
  (state: App.State) => state.checkout.accommodationSessions,
  (state: App.State) => getFlightBundledItemIds(state),
  (state: App.State) => getTourV2Items(state),
  (state: App.State) => getCruiseItems(state),
  (state: App.State) => getLppMemberPricingEnabledForCheckout(state),
  (
    cartItems,
    properties,
    sessions,
    bundledItemIds,
    tourV2Items,
    cruiseItems,
    lppMemberPricingEnabled,
  ): App.WithDataStatus<Array<App.Checkout.AccommodationV2AccommodationOfferView>> => {
    const itemsByBookingKey = groupBy(cartItems, item => `${item.offerId}-${item.propertyId}-${item.checkIn}-${item.checkOut}`)
    const itemGroups = [...itemsByBookingKey.values()].map(items => ({
      checkIn: items[0].checkIn,
      checkOut: items[0].checkOut,
      offerId: items[0].offerId,
      propertyId: items[0].propertyId,
      items,
    }))

    const views = itemGroups.map((itemGroup): App.WithDataStatus<App.Checkout.AccommodationV2AccommodationOfferView | undefined> => {
      const { offerId, checkIn, checkOut, propertyId, items } = itemGroup
      const property = properties[propertyId]?.property
      const intl = getCurrentIntl()
      const itemViewsWithStatus = items.map(item => getAccommodationV2ItemView(
        intl,
        item,
        item.sessionId ? sessions[item.sessionId] : undefined,
        property,
        lppMemberPricingEnabled,
      ))

      const duration = diffInDays(new Date(checkIn), new Date(checkOut))
      const bundledWithTour = items.some(item => !!item.bundledItemIds?.length)
      const itemBundleId = items[0]?.bundledItemIds?.[0]
      const designation = bundledWithTour
        ? getAccommodationDesignationWithTourBundle(checkIn, itemBundleId, tourV2Items)
        : getAccommodationDesignationWithCruise(checkIn, checkOut, cruiseItems)

      const offerType = getAccommodationV2ItemService(items[0], sessions) === 'bedbank'
        ? OFFER_TYPE_BED_BANK
        : OFFER_TYPE_ALWAYS_ON
      const offerPageParams = new URLSearchParams({
        [queryKeyCheckIn]: checkIn,
        [queryKeyCheckOut]: checkOut,
      })

      const view: App.Checkout.AccommodationV2AccommodationOfferView = {
        offerId,
        offerPageUrl: getHotelOfferUrl(offerId, offerType, offerPageParams),
        mainLabel: property?.name ?? '',
        subLabel: property?.headline,
        bedGroups: [],
        confidenceLabels: [],
        duration,
        startDate: checkIn,
        endDate: checkOut,
        image: property?.images[0] ?? {},
        location: property?.location.title || property?.location.formattedAddress || '',
        locationCountry: property?.location.countryName,
        locationCountryCode: property?.location.countryCode,
        locationStateProvince: property?.location.region,
        occupancy: items.map(item => item.occupancy).filter(excludeNullOrUndefined),
        offerType,
        reservationType: RESERVATION_TYPE_INSTANT_BOOKING,
        urgencyLabels: [],
        saleUnit: 'room',
        durationLabel: intl.formatMessage(nightCountMessage, { count: duration }),
        offerLoaded: !!property,
        itemViews: itemViewsWithStatus.map(itemView => itemView.data),
        propertyTimezone: property?.location.timezone ?? '',
        bundledWithFlightsOnly: false,
        bundledWithTour,
        designation,
        isBundled: items.some(item => bundledItemIds.has(item.itemId)),
        hasMobilePromotion: itemViewsWithStatus.some(itemView => itemView.data.rate?.isMobilePromotion),
        propertyId,
        property,
      }

      return {
        data: view,
        hasRequiredData: itemViewsWithStatus.every(itemView => itemView.hasRequiredData),
      }
    })

    return {
      data: nonNullable(views.map(view => view.data)),
      hasRequiredData: views.every(view => view.hasRequiredData),
    }
  },
)

export default getAccommodationV2ItemViews
